import React from "react";
import PropTypes from "prop-types";
import { Table } from "components/Table";
import { getStockCompanyIcon } from "utils/index";
import AssetAllocationBars from "shared/StrategyOverview/Components/AssetAllocationBars";
import {
  AssetsContainer,
  AssetTableWrapper,
  AssetLogoContainer,
  AssetLogoImage,
  AssetLogoFallback,
  AssetCell,
  AssetInfo,
  AssetSymbol,
  AssetCompany,
  AssetText,
  AssetValueContainer,
  AssetValue,
  AssetShares,
  StrategyBadgesContainer,
  StrategyBadge,
  EmptyMessage,
} from "./StrategyTreemap.styles";

const renderAssetLogo = (asset) => {
  const symbol = asset.symbol || asset.stock || asset.name || "--";
  const logoUrl =
    getStockCompanyIcon(symbol) ||
    `https://logo.clearbit.com/${symbol.toLowerCase()}.com`;
  return (
    <AssetLogoContainer>
      <AssetLogoImage
        src={logoUrl}
        alt={symbol}
        onError={(e) => {
          e.target.style.display = "none";
          const fallback = e.target.nextSibling;
          if (fallback) fallback.style.display = "flex";
        }}
      />
      <AssetLogoFallback>{symbol.charAt(0).toUpperCase()}</AssetLogoFallback>
    </AssetLogoContainer>
  );
};

const assetsColumns = [
  {
    dataField: "asset",
    text: "Assets",
    props: {
      cell: ({ row }) => {
        const asset = row.original;
        return (
          <AssetCell>
            {renderAssetLogo(asset)}
            <AssetInfo>
              <AssetSymbol>
                {asset.symbol || asset.stock || asset.name || "N/A"}
              </AssetSymbol>
              <AssetCompany>
                {asset.company_name ||
                  asset.company ||
                  asset.symbol ||
                  asset.stock}
              </AssetCompany>
            </AssetInfo>
          </AssetCell>
        );
      },
    },
  },
  {
    dataField: "weight",
    text: "Weight",
    props: {
      cell: ({ row }) => {
        const asset = row.original;
        return (
          <AssetText>
            {asset.weight || asset.percentage || asset.percent}
          </AssetText>
        );
      },
    },
  },
  {
    dataField: "sharePrice",
    text: "Share price",
    props: {
      cell: ({ row }) => {
        const asset = row.original;
        return <AssetText>{asset.sharePrice || asset.price}</AssetText>;
      },
    },
  },
  {
    dataField: "value",
    text: "Value",
    props: {
      cell: ({ row }) => {
        const asset = row.original;
        return (
          <AssetValueContainer>
            <AssetValue>{asset.value}</AssetValue>
            <AssetShares>{asset.shares} shares</AssetShares>
          </AssetValueContainer>
        );
      },
    },
  },
  {
    dataField: "fromStrategy",
    text: "From strategy",
    props: {
      cell: ({ row }) => {
        const asset = row.original;
        const assetStrategies = asset.strategies || [
          asset.fromStrategy || asset.from_strategy,
        ];
        return (
          <StrategyBadgesContainer>
            {assetStrategies.filter(Boolean).map((s, idx) => (
              <StrategyBadge key={idx}>{s}</StrategyBadge>
            ))}
          </StrategyBadgesContainer>
        );
      },
    },
  },
];

const AssetsView = ({ assets, assetBarsData, isLoadingAssets }) => {
  if (!isLoadingAssets && !assets.length) {
    return (
      <AssetsContainer>
        <EmptyMessage>
          No assets data available. Please adjust your allocation settings.
        </EmptyMessage>
      </AssetsContainer>
    );
  }

  return (
    <AssetsContainer>
      <AssetAllocationBars assets={assetBarsData} isLoading={isLoadingAssets} />
      <AssetTableWrapper>
        <Table
          data={assets}
          columns={assetsColumns}
          isLoading={isLoadingAssets}
          showPagination={false}
          notFoundText="No assets available"
        />
      </AssetTableWrapper>
    </AssetsContainer>
  );
};

AssetsView.propTypes = {
  assets: PropTypes.array.isRequired,
  assetBarsData: PropTypes.array.isRequired,
  isLoadingAssets: PropTypes.bool.isRequired,
};

export default AssetsView;
